import React, { useEffect, useState } from "react";
import style from "./timeStampConfig.module.css";
import { ErrorToast, SuccessToast } from "../../../components/toastMessage";
import { BASE_URL } from "../../../constants/Constants";
import BoxComponent from "../../../components/CommonComponents/BoxComponent/BoxComponent";
import { _axios } from "../../../utils/axios";
import { useNavigate } from "react-router-dom";

interface TimeConfigState {
    level1Enabled: boolean;
    level1Days: string;
    level2Enabled: boolean;
    level2Days: string;
    level3Enabled: boolean;
    level3Days: string;
}

const TimeStampConfig = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false);
    const [formFields, setFormFields] = useState<TimeConfigState>({
        level1Enabled: false,
        level1Days: "",
        level2Enabled: false,
        level2Days: "",
        level3Enabled: false,
        level3Days: "",
    });

    const fetchData = () => {
        setLoading(true);
        _axios.get(`${BASE_URL}/api/time-stamp-config/get-time-stamp-config`)
            .then((response) => {
                if (response?.data?.success === 0) {
                    ErrorToast(response?.data?.message);
                    return;
                }
                const data = response.data.data[0];
                // Update formFields with fetched data
                setFormFields({
                    level1Enabled: data.level1_enabled === 1,
                    level1Days: data.level1_days?.toString() || "0",
                    level2Enabled: data.level2_enabled === 1,
                    level2Days: data.level2_days?.toString() || "0",
                    level3Enabled: data.level3_enabled === 1,
                    level3Days: data.level3_days?.toString() || "0",
                });
            })
            .catch((error) => {
                if (error?.response?.data?.message) {
                    ErrorToast(error?.response?.data?.message);
                } else {
                    ErrorToast("Failed to fetch configuration data");
                }
                console.log('error', error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        // Fetch data from the backend when the component mounts
        fetchData();
    }, []);

    const handleToggleChange = (field: string) => {
        setFormFields(prev => ({
            ...prev,
            [field]: !prev[field as keyof TimeConfigState]
        }));
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        // Don't allow negative values
        if (value !== "" && Number(value) < 0) {
            return;
        }

        setFormFields(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        // Check if level1 is enabled and days is empty or invalid
        if (formFields.level1Enabled && (!formFields.level1Days || isNaN(Number(formFields.level1Days)))) {
            ErrorToast("Please enter valid number of days for Level 1");
            return false;
        }

        // Check if level2 is enabled and days is empty or invalid
        if (formFields.level2Enabled && (!formFields.level2Days || isNaN(Number(formFields.level2Days)))) {
            ErrorToast("Please enter valid number of days for Level 2");
            return false;
        }

        // Check that all values are non-negative if provided
        if ((formFields.level1Days && Number(formFields.level1Days) < 0) ||
            (formFields.level2Days && Number(formFields.level2Days) < 0) ||
            (formFields.level3Days && Number(formFields.level3Days) < 0)) {
            ErrorToast("Days values cannot be negative");
            return false;
        }

        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            // Ensure all days values are integers (0 if empty or toggle is off)
            const level1Days = formFields.level1Enabled && formFields.level1Days ? parseInt(formFields.level1Days) : 0;
            const level2Days = formFields.level2Enabled && formFields.level2Days ? parseInt(formFields.level2Days) : 0;
            const level3Days = formFields.level3Days ? parseInt(formFields.level3Days) : 0;

            const response = await _axios.post(
                `${BASE_URL}/api/time-stamp-config/update-time-stamp-config`,
                {
                    level1_enabled: formFields.level1Enabled ? 1 : 0,
                    level1_days: level1Days,
                    level2_enabled: formFields.level2Enabled ? 1 : 0,
                    level2_days: level2Days,
                    level3_enabled: formFields.level3Enabled ? 1 : 0,
                    level3_days: level3Days,
                }
            );

            if (response?.data?.success === 0) {
                ErrorToast(response?.data?.message);
                return;
            }

            SuccessToast('Time Stamp Configuration updated successfully');
            fetchData(); // Refresh data after update
        } catch (error: any) {
            if (error?.response?.data?.message) {
                ErrorToast(error?.response?.data?.message);
            } else {
                ErrorToast("Failed to update configuration");
            }
            console.error("Error updating data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Navigate to Dashboard
        navigate("/dashboard");
    };

    return (
        <div className={style.body}>
            <BoxComponent>
                <div className={style.bodyContanier}>
                    <div className={style.levelContainer}>
                        <div className={style.overrideActive}>
                            <label>Level 1 time sensitivity flag</label>
                            <label className={style.toggleBtn}>
                                <input
                                    type="checkbox"
                                    className={style.inputCheckbox}
                                    checked={formFields.level1Enabled}
                                    onChange={() => handleToggleChange("level1Enabled")}
                                />
                                <span className={style.slider}></span>
                            </label>

                            {formFields.level1Enabled && (
                                <div className={style.inputContainer}>
                                    <label>Days after an action will be taken</label>
                                    <input
                                        type="number"
                                        name="level1Days"
                                        placeholder='Enter Days'
                                        value={formFields.level1Days}
                                        onChange={handleInputChange}
                                        min="0"
                                        required
                                    />
                                </div>
                            )}
                        </div>
                        {formFields.level1Enabled && (
                            <small className={style.noteText}>Note: System will exclude weekends while counting the days.</small>
                        )}
                    </div>

                    <div className={style.levelContainer}>
                        <div className={style.overrideActive}>
                            <label>Level 2 time sensitivity flag</label>
                            <label className={style.toggleBtn}>
                                <input
                                    type="checkbox"
                                    className={style.inputCheckbox}
                                    checked={formFields.level2Enabled}
                                    onChange={() => handleToggleChange("level2Enabled")}
                                />
                                <span className={style.slider}></span>
                            </label>

                            {formFields.level2Enabled && (
                                <div className={style.inputContainer}>
                                    <label>Days after an action will be taken</label>
                                    <input
                                        type="number"
                                        name="level2Days"
                                        placeholder='Enter Days'
                                        value={formFields.level2Days}
                                        onChange={handleInputChange}
                                        min="0"
                                        required
                                    />
                                </div>
                            )}
                        </div>
                        {formFields.level2Enabled && (
                            <small className={style.noteText}>Note: System will exclude weekends while counting the days.</small>
                        )}
                    </div>

                    <div className={style.levelContainer}>
                        <div className={style.overrideActive}>
                            <label>Level 3 time sensitivity flag</label>
                            <label className={style.toggleBtn}>
                                <input
                                    type="checkbox"
                                    className={style.inputCheckbox}
                                    checked={formFields.level3Enabled}
                                    onChange={() => handleToggleChange("level3Enabled")}
                                />
                                <span className={style.slider}></span>
                            </label>

                            {/* Level 3 days input always displays regardless of toggle state */}
                            <div className={style.inputContainer}>
                                <label>Days after an action will be taken</label>
                                <input
                                    type="number"
                                    name="level3Days"
                                    placeholder='Enter Days'
                                    value={formFields.level3Days}
                                    onChange={handleInputChange}
                                    min="0"
                                />
                            </div>
                        </div>
                        <small className={style.noteText}>Note: System will exclude weekends while counting the days.</small>
                    </div>

                    <div className={style.buttonSection}>
                        <button type='submit'
                            className={'bg-primary ml-4 px-6 py-2 font-bold text-white rounded-full disabled:bg-gray-300 disabled:text-gray-500 mb-5'} onClick={handleSave}
                            disabled={loading}>
                            Publish
                        </button>

                        <button type='submit'
                            className={'bg-black ml-4 px-6 py-2 font-bold text-white rounded-full disabled:bg-gray-300 disabled:text-gray-500 mb-5'}
                            onClick={handleClose}>
                            Close
                        </button>
                    </div>
                </div>
            </BoxComponent>
        </div>
    );
};

export default TimeStampConfig;
